import {
  WSElement,
  WSPage,
  WSPageToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { WorkLogStatus } from "@wingspanhq/payments/dist/interfaces";
import { useEffect, useRef } from "react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation
} from "react-router-dom";
import { useUserId } from "../../../query/hooks/helpers";
import { useQueryWorkLogsSummary } from "../../../query/workLog/queries/useQueryWorkLogsSummary";
import { Tabs } from "../../../components/Tabs";
import { INVOICING_PATH } from "../../Invoicing";
import { FilterableTableWorkLogsPayeeView } from "../components/TableWorkLogsPayeeView/FilterableTableWorkLogsPayeeView";
import { useModalCreateWorkLog } from "../modals/ModalCreateWorkLog";
import {
  PATH_WORK_LOGS_PAYEE,
  PATH_WORK_LOGS_PAYEE_TAB_IN_PROGRESS,
  PATH_WORK_LOGS_PAYEE_TAB_SUBMITTED
} from "../paths";

const useOpenCreateWorkLogModalFromUrl = () => {
  const history = useHistory();
  const location = useLocation();
  const modalCreateWorkLog = useModalCreateWorkLog();
  const hasOpenedModalRef = useRef(false);

  useEffect(() => {
    if (hasOpenedModalRef.current) {
      return;
    }

    const params = new URLSearchParams(location.search);
    const payerId = params.get("payerId");
    const engagementId = params.get("engagementId");

    if (payerId && engagementId) {
      hasOpenedModalRef.current = true;

      // Clear query params from URL
      history.replace(location.pathname);

      modalCreateWorkLog.open({
        defaultPayerId: payerId,
        defaultPayerPayeeEngagementId: engagementId
      });
    }
  }, [history, location.pathname, location.search, modalCreateWorkLog]);
};

export const RoutePayeeLogs = () => {
  const history = useHistory();
  const modalCreateWorkLog = useModalCreateWorkLog();
  const userId = useUserId();

  useOpenCreateWorkLogModalFromUrl();

  // Summary queries for tab counts
  const queryWorkLogsSummaryInProgress = useQueryWorkLogsSummary({
    "filter[payeeId]": userId,
    "filter[status][=]": WorkLogStatus.Draft
  });

  const queryWorkLogsSummarySubmitted = useQueryWorkLogsSummary({
    "filter[payeeId]": userId,
    "filter[status][=]": WorkLogStatus.Approved
  });

  return (
    <WSPage>
      <WSElement mb="2XL">
        <WSPageToolbar
          title="Work logs"
          actions={[
            {
              label: "New work log",
              buttonKind: "Primary",
              onClick: () => modalCreateWorkLog.open()
            }
          ]}
        />

        <WSText.ParagraphSm color="gray500">
          Track you work and submit. Submitted work logs will generate invoices
          on your{" "}
          <a
            onClick={() => {
              history.push(INVOICING_PATH);
            }}
          >
            Invoices page
          </a>
          .
        </WSText.ParagraphSm>
      </WSElement>

      <Tabs
        mb="2XL"
        tabs={[
          {
            label: "In-progress",
            path: PATH_WORK_LOGS_PAYEE_TAB_IN_PROGRESS,
            badge: queryWorkLogsSummaryInProgress.data?.listSize
          },
          {
            label: "Submitted",
            path: PATH_WORK_LOGS_PAYEE_TAB_SUBMITTED,
            badge: queryWorkLogsSummarySubmitted.data?.listSize
          }
        ]}
      />

      <Switch>
        <Route path={PATH_WORK_LOGS_PAYEE_TAB_IN_PROGRESS}>
          <FilterableTableWorkLogsPayeeView status="in-progress" />
        </Route>

        <Route path={PATH_WORK_LOGS_PAYEE_TAB_SUBMITTED}>
          <FilterableTableWorkLogsPayeeView status="submitted" />
        </Route>

        <Redirect
          from={PATH_WORK_LOGS_PAYEE}
          to={PATH_WORK_LOGS_PAYEE_TAB_IN_PROGRESS}
        />
      </Switch>
    </WSPage>
  );
};
